import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminApiClient } from "@/lib/api";
import { SEO } from "@/components/seo/SEO";
import { FIRM } from "@/constants/firmInfo";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      // The backend responds with a Set-Cookie header (httpOnly session
      // cookie); withCredentials ensures the browser stores it. We don't
      // read or store the token client-side at all.
      await adminApiClient.post("/admin/login", form);
      navigate("/admin/leads");
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Admin Login" description="Staff login for Rinnou & Associates, P.C." path="/admin" />
      <div className="min-h-[80vh] grid place-items-center bg-[color:var(--paper-2)] px-4">
        <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-[0_10px_30px_rgba(15,23,42,0.08)]">
          <div className="flex flex-col items-center mb-6">
            <img src={FIRM.logoSeal} alt="Firm seal" className="h-14 w-14 object-contain mb-3" />
            <h1 className="font-heading text-xl text-[color:var(--navy-950)]">Staff Login</h1>
            <p className="text-xs text-slate-500 mt-1">Access the leads dashboard</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <Label htmlFor="admin-email">Email</Label>
              <Input
                id="admin-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 bg-white border-slate-300"
                data-testid="admin-login-email-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="admin-password">Password</Label>
              <Input
                id="admin-password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 bg-white border-slate-300"
                data-testid="admin-login-password-input"
                required
              />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[color:var(--navy-950)] hover:bg-[color:var(--navy-900)] text-white font-semibold h-11"
              data-testid="admin-login-submit-button"
            >
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in...</> : <><Lock className="h-4 w-4" /> Sign In</>}
            </Button>
          </form>
        </div>
      </div>
    </>
  );
}
